import React from 'react'
import { Spring, Trail, config, animated } from 'react-spring/renderprops.cjs'
import cx from 'classnames'
import { isMobile } from '/js/util'
import Grid from '/img/svg/grid'
import Download from '/img/svg/download'
import Close from '/img/svg/close'
import Star from '/img/svg/star'
import Envelope from '/img/svg/envelope'
import CaratLeft from '/img/svg/carat-left'
import OpenArrow from '/img/svg/open-arrow'
import YouTube from '/img/svg/you-tube'
import Facebook from '/img/svg/facebook'
import Instagram from '/img/svg/instagram'
import LinkedIn from '/img/svg/linked-in'

class Navigation extends React.Component {
  constructor(props) {
    super(props)

    this.navRef = React.createRef()
    this.navOverlayRef = React.createRef()

    this.scrollingEl = !!document.scrollingElement
      ? document.scrollingElement
      : document.body

    const { currentPage, theme } = this.props
    this.isHomepage = currentPage.location.pathname === '/'
    const isDarkTheme = theme.bgColor === 'bg-danaher-blue' || this.isHomepage
    const buttonColors = cx({
      'hover:bg-smoke-white focus:bg-smoke-white hover:text-jet-black focus:text-jet-black': isDarkTheme,
      'hover:bg-jet-black focus:bg-jet-black hover:text-white focus:text-white': !isDarkTheme
    })

    const navColors = cx({
      'text-white': isDarkTheme,
      'text-jet-black': !isDarkTheme
    })

    this.state = {
      isDownloadsOpen: false,
      isExploreOpen: false
    }

    this.navClasses = {
      nav: navColors,
      navSpacing: 'py-15 px-25 block',
      navButton: `uppercase flex items-center ${buttonColors} transition-fast`,
      menu:
        'absolute top-57 md:top-auto md:bottom-57 bg-jet-black w-full lg:h-auto md:w-305 z-20 overflow-hidden text-white flex flex-col max-h-screen',
      menuTrack: 'px-32 pb-60 pt-15 md:pb-15 overflow-y-auto',
      menuItem:
        'group hover:underline py-15 flex justify-between border-granite-gray items-center'
    }
  }

  componentDidMount() {
    document.body.addEventListener('click', this.closeMenus)
    this.determineScreenLock()
  }

  componentDidUpdate(prevState) {
    const { isDownloadsOpen, isExploreOpen } = this.state
    if (
      isDownloadsOpen !== prevState.isDownloadsOpen ||
      isExploreOpen !== prevState.isExploreOpen
    ) {
      this.determineScreenLock()
    }
  }

  componentWillUnmount() {
    document.body.removeEventListener('click', this.closeMenus)
  }

  closeMenus = e => {
    const clickIsOffNav = !this.navRef.current.contains(event.target)

    if (clickIsOffNav) {
      this.setState({ isDownloadsOpen: false, isExploreOpen: false })
    }
  }

  determineScreenLock = () => {
    const { isExploreOpen, isDownloadsOpen } = this.state
    const isSomethingOpen = isExploreOpen || isDownloadsOpen

    if (isSomethingOpen) {
      this.scrollingEl.classList.add('overflow-hidden')
      this.navOverlayRef.current.classList.add(
        'opacity-50',
        'pointer-events-auto',
        'z-50'
      )
      this.navOverlayRef.current.classList.remove(
        'opacity-0',
        'pointer-events-none',
        'z-0'
      )
    } else {
      this.scrollingEl.classList.remove('overflow-hidden')
      this.navOverlayRef.current.classList.add(
        'opacity-0',
        'pointer-events-none',
        'z-0'
      )
      this.navOverlayRef.current.classList.remove(
        'opacity-50',
        'pointer-events-auto',
        'z-50'
      )
    }
  }

  renderPillarNavItems() {
    const { currentPage, routes } = this.props

    const currentPath = currentPage.location.pathname
      ? currentPage.location.pathname
      : currentPage.pathname

    return routes.map((route, index) => {
      if (route.type === 'pillar') {
        return (
          <li key={`pillar-nav-${index}`}>
            <a
              href={route.url}
              data-track-gtm={`Navigation|${route.title}`}
              className={cx(
                this.navClasses.navSpacing,
                this.navClasses.navButton,
                {
                  'bg-jet-black text-white': route.url === currentPath
                }
              )}
            >
              {route.title}
            </a>
          </li>
        )
      }
    })
  }

  renderPillarNav() {
    if (isMobile()) {
      return null
    }

    return <ul className="flex items-center">{this.renderPillarNavItems()}</ul>
  }

  renderNavItemIcon(item) {
    const isEmail = item.url.startsWith('mailto')
    const isExternal = !item.url.startsWith('/')

    if (isEmail) {
      return (
        <span>
          <Envelope className="w-25" />
        </span>
      )
    }

    if (isExternal) {
      return (
        <span>
          <OpenArrow />
        </span>
      )
    }

    return null
  }

  renderNavItems() {
    const { isExploreOpen } = this.state
    const { routes } = this.props
    const { external_links } = this.props.navigation

    const navRoutes = !isMobile()
      ? routes.filter(route => {
          return route.type != 'pillar' && route.hasOwnProperty('type')
        })
      : routes.filter(route => {
          return route.hasOwnProperty('type')
        })

    const navItems = navRoutes.concat(external_links)

    return (
      <Trail
        items={navItems}
        keys={item => item.url}
        to={{
          transform: isExploreOpen
            ? 'translate3d(0,0px,0)'
            : 'translate3d(-10px,0,0)'
        }}
        config={config.stiff}
      >
        {(item, index) => props => (
          <animated.li style={props} key={`nav-item-${index}`}>
            <a
              className={`${this.navClasses.menuItem} border-t ${
                item.type === 'pillar' ? 'pl-25' : ''
              }`}
              data-track-gtm={`Menu|${item.title}`}
              href={item.url}
              tabIndex={isExploreOpen ? 0 : -1}
              target={item.type === 'external' ? '_blank' : '_self'}
              rel={item.type === 'external' ? 'noopener' : ''}
            >
              {item.title}
              {this.renderNavItemIcon(item)}
            </a>
          </animated.li>
        )}
      </Trail>
    )
  }

  renderExternalItems() {
    const { isExploreOpen } = this.state
    const { external_links } = this.props.navigation

    return (
      <Trail
        items={external_links}
        keys={item => item.url}
        to={{
          transform: isExploreOpen
            ? 'translate3d(0,0px,0)'
            : 'translate3d(-10px,0,0)'
        }}
        config={config.stiff}
      >
        {(item, index) => props => (
          <animated.li style={props} key={`external-item-${index}`}>
            <a
              className={`${this.navClasses.menuItem} border-t`}
              href={item.url}
              data-track-gtm={`Menu|${item.title}`}
              tabIndex={isExploreOpen ? 0 : -1}
            >
              {item.title}
            </a>
          </animated.li>
        )}
      </Trail>
    )
  }

  renderSocialItems() {
    const { isExploreOpen } = this.state
    const { social } = this.props

    return social.map((link, index) => (
      <a
        href={link.url}
        key={`social-item-${index}`}
        data-track-gtm={`Menu|${link.title}`}
        className={index < social.length - 1 ? 'mr-20' : ''}
        tabIndex={isExploreOpen ? 0 : -1}
        target="_blank"
      >
        {link.title === 'Facebook' && <Facebook />}
        {link.title === 'Twitter' && <Twitter />}
        {link.title === 'LinkedIn' && <LinkedIn />}
        {link.title === 'Instagram' && <Instagram />}
        {link.title === 'YouTube' && <YouTube />}
      </a>
    ))
  }

  renderExploreMenu = () => {
    const { isExploreOpen } = this.state

    return (
      <>
        <Spring
          to={{
            transform: isExploreOpen
              ? 'translate3d(0,0,0)'
              : 'translate3d(-100%,0,0)'
          }}
          config={{ velocity: isExploreOpen ? 0 : 20 }}
        >
          {props => (
            <animated.div
              style={props}
              className={`left-0 ${this.navClasses.menu}`}
            >
              <div className={this.navClasses.menuTrack}>
                <ul>
                  <li>
                    <a
                      href="/"
                      data-track-gtm="Menu|Home"
                      className={this.navClasses.menuItem}
                      style={{ justifyContent: 'flex-start' }}
                      tabIndex={isExploreOpen ? 0 : -1}
                    >
                      <CaratLeft /> Return Home
                    </a>
                  </li>
                  {this.renderNavItems()}
                </ul>
                <ul
                  className={`flex items-center ${this.navClasses.menuItem}`}
                  style={{ justifyContent: 'flex-start' }}
                >
                  {this.renderSocialItems()}
                </ul>
              </div>
            </animated.div>
          )}
        </Spring>
      </>
    )
  }

  renderExploreButton() {
    const { isExploreOpen } = this.state

    return (
      <button
        className={`${this.navClasses.navButton} ${this.navClasses.navSpacing}`}
        onClick={() =>
          this.setState({
            isExploreOpen: !this.state.isExploreOpen,
            isDownloadsOpen: false
          })
        }
      >
        {isExploreOpen && (
          <>
            <Close /> <span className="ml-5 block">Close</span>
          </>
        )}
        {!isExploreOpen && (
          <>
            <Grid /> <span className="ml-5 block">Explore</span>
          </>
        )}
      </button>
    )
  }

  renderDownloadItems() {
    const { isDownloadsOpen } = this.state
    const { pdfs } = this.props.navigation

    return (
      <Trail
        items={pdfs}
        keys={item => item.url}
        to={{
          transform: isDownloadsOpen
            ? 'translate3d(0,0px,0)'
            : 'translate3d(10px,0,0)'
        }}
        config={config.stiff}
      >
        {(item, index) => props => (
          <animated.li style={props} key={`download-item-${index}`}>
            <a
              className={`${this.navClasses.menuItem} ${
                index != 0 ? 'border-t' : ''
              }`}
              href={item.url}
              tabIndex={isDownloadsOpen ? 0 : -1}
            >
              {item.title}
              {item.isCurrent && (
                <span>
                  <Star />
                </span>
              )}
            </a>
          </animated.li>
        )}
      </Trail>
    )
  }

  renderDownloadsMenu = () => {
    const { isDownloadsOpen } = this.state

    return (
      <>
        <Spring
          to={{
            transform: isDownloadsOpen
              ? 'translate3d(0,0,0)'
              : 'translate3d(100%,0,0)'
          }}
          config={{ velocity: isDownloadsOpen ? 0 : 20 }}
        >
          {props => (
            <animated.div
              style={props}
              className={`right-0 ${this.navClasses.menu}`}
            >
              <ul className={this.navClasses.menuTrack}>
                {this.renderDownloadItems()}
              </ul>
            </animated.div>
          )}
        </Spring>
      </>
    )
  }

  renderDownloadsButton() {
    const { isDownloadsOpen } = this.state
    return (
      <button
        className={`${this.navClasses.navButton} ${this.navClasses.navSpacing}`}
        onClick={() =>
          this.setState({
            isDownloadsOpen: !this.state.isDownloadsOpen,
            isExploreOpen: false
          })
        }
      >
        {isDownloadsOpen && (
          <>
            <Close /> <span className="ml-5 block">Close</span>
          </>
        )}
        {!isDownloadsOpen && (
          <>
            <Download /> <span className="ml-5 block">Download</span>
          </>
        )}
      </button>
    )
  }

  render() {
    const { theme } = this.props
    const bgColor = this.isHomepage ? 'bg-jet-black' : theme.bgColor

    return (
      <>
        <div
          className="bg-jet-black inset-0 fixed transition-fast opacity-0 pointer-events-none z-0"
          ref={this.navOverlayRef}
        ></div>
        <nav
          className="fixed top-0 md:bottom-0 md:top-auto left-0 right-0 text-18 uppercase font-condensed z-60"
          role="navigation"
          ref={this.navRef}
        >
          <div
            className={`${bgColor} ${this.navClasses.nav} w-full flex justify-between`}
          >
            {this.renderExploreButton()}
            {this.renderExploreMenu()}
            {this.renderPillarNav()}
            {this.renderDownloadsButton()}
            {this.renderDownloadsMenu()}
          </div>
        </nav>
      </>
    )
  }
}

export default Navigation
